'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';

import IconButton from '../IconButton/IconButton';
import Typography from '../Typography/Typography';
import Box from '../Box/Box';
import Iconography from '../Iconography/Iconography';
import usePortalPosition from '../../../hooks/usePortalPosition';

import {
  getWeekStart,
  getWeekdayLabels,
  getLocalizedDatePlaceholder,
  getNormalizedDate,
  parseDateInput,
  getMonthNames,
} from './utils';

import styles from './DatePicker.module.scss';

// Type definitions
type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';
type Size = 'small' | 'medium' | 'large';
type Locale =
  | 'en-US'
  | 'en-CA'
  | 'fr-CA'
  | 'de-DE'
  | 'nl-NL'
  | 'de-CH'
  | 'fr-CH'
  | 'nb-NO'
  | 'en-EU'
  | 'en-AE'
  | 'ar-AE'
  | 'en-SA'
  | 'ar-SA'
  | 'fr-BE';

interface ValidationResponse {
  validationType?: string;
  message?: string;
}

interface CalendarDay {
  day: number;
  currentMonth: boolean;
  date: Date;
}

export interface DatePickerProps {
  value?: Date | string | null;
  onChange?: (date: Date) => void;
  locale?: Locale;
  label?: string;
  helperText?: string;
  direction?: Direction;
  theme?: Theme;
  size?: Size;
  required?: boolean;
  name: string;
  selectableDates?: string[];
  /**
   * Minimum selectable date. Dates before this will be disabled.
   * Can be a Date object, ISO string, or 'today' for current date.
   */
  minDate?: Date | string;
  /**
   * Maximum selectable date. Dates after this will be disabled.
   * Can be a Date object, ISO string, or 'today' for current date.
   */
  maxDate?: Date | string;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  /**
   * Whether the date picker is disabled.
   */
  disabled?: boolean;
  /**
   * Callback when the clear button is clicked.
   * The clear button is always shown when a date is selected and the picker is not disabled.
   */
  onClear?: () => void;
  /**
   * Callback when date picker loses focus (calendar closes)
   */
  onBlur?: () => void;
  /**
   * Language code for month/weekday labels (e.g. 'en', 'de', 'fr', 'es').
   * When provided, overrides the locale for display labels only.
   * Date formatting and week start day still follow `locale`.
   */
  language?: string;
  /**
   * Controls how users navigate months/years in the calendar header.
   * - `'default'`: static text with prev/next arrows
   * - `'dropdown'`: month and year dropdown selects for quick navigation.
   *   Useful for date-of-birth or distant-date entry.
   *   Year range is derived from minDate/maxDate (defaults: 1900 to current year).
   * @default 'default'
   */
  navigationMode?: 'default' | 'dropdown';
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  locale = 'en-US',
  label,
  helperText,
  direction = 'ltr',
  theme = 'dark',
  size = 'medium',
  required = false,
  name,
  selectableDates = [],
  minDate,
  maxDate,
  validationResponse,
  showValidation = true,
  disabled = false,
  onClear,
  onBlur,
  language,
  navigationMode = 'default',
}) => {
  // Ensure locale is never empty
  const effectiveLocale = locale || 'en-US';
  // Use language for display labels (month/weekday), falling back to locale
  const displayLocale = language || effectiveLocale;
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (!value) {
      return null;
    }

    const initialDate = parseDateInput(value);
    return Number.isNaN(initialDate.getTime()) ? null : initialDate;
  });
  const [calendarDate, setCalendarDate] = useState(() => {
    if (!value) {
      // Create today's date at UTC midnight for consistency
      const now = new Date();
      return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    }

    const initialDate = parseDateInput(value);
    if (Number.isNaN(initialDate.getTime())) {
      const now = new Date();
      return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    }
    return initialDate;
  });
  // Create currentDate at UTC midnight for consistency with calendar date comparisons
  const currentDate = useMemo(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  }, []);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Track the last synced value to prevent re-render loops
  const lastSyncedValueRef = useRef<string | null>(null);

  const handleCloseCalendar = useCallback(() => {
    setShowCalendar(false);
    onBlur?.();
  }, [onBlur]);

  const { position: calendarPosition, shouldOpenAbove } = usePortalPosition({
    isOpen: showCalendar,
    triggerRef: inputContainerRef,
    popoverRef: dropdownRef,
    estimatedHeight: 380,
    offset: 8,
    minWidth: 320,
    onClose: handleCloseCalendar,
  });

  // Compute minimum date for disabling past dates (using UTC to avoid timezone issues)
  const computedMinDate = useMemo(() => {
    if (!minDate) return null;
    if (minDate === 'today') {
      const now = new Date();
      return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    }
    // Use parseDateInput to handle YYYY-MM-DD strings correctly as UTC
    const parsed = parseDateInput(minDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, [minDate]);

  // Compute maximum date for disabling future dates (using UTC to avoid timezone issues)
  const computedMaxDate = useMemo(() => {
    if (!maxDate) return null;
    if (maxDate === 'today') {
      const now = new Date();
      return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    }
    // Use parseDateInput to handle YYYY-MM-DD strings correctly as UTC
    const parsed = parseDateInput(maxDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, [maxDate]);

  const isBeforeMinDate = (date: Date): boolean => {
    if (!computedMinDate) return false;
    // Compare using UTC normalized dates to avoid timezone issues
    const compareDateStr = getNormalizedDate(date);
    const minDateStr = getNormalizedDate(computedMinDate);
    return compareDateStr < minDateStr;
  };

  const isAfterMaxDate = (date: Date): boolean => {
    if (!computedMaxDate) return false;
    // Compare using UTC normalized dates to avoid timezone issues
    const compareDateStr = getNormalizedDate(date);
    const maxDateStr = getNormalizedDate(computedMaxDate);
    return compareDateStr > maxDateStr;
  };

  const startDay = getWeekStart(effectiveLocale);
  const weekdays = getWeekdayLabels(displayLocale, startDay);
  const currentMonth = calendarDate.getUTCMonth();
  const currentYear = calendarDate.getUTCFullYear();

  // Month/year dropdown support
  const monthNames = useMemo(() => getMonthNames(displayLocale), [displayLocale]);

  const yearRange = useMemo(() => {
    const minYear = computedMinDate ? computedMinDate.getUTCFullYear() : 1900;
    const maxYear = computedMaxDate ? computedMaxDate.getUTCFullYear() : new Date().getFullYear();
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  }, [computedMinDate, computedMaxDate]);

  const isMonthDisabledInDropdown = useCallback(
    (month: number): boolean => {
      const year = calendarDate.getUTCFullYear();
      const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0));
      const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
      if (computedMinDate && lastDayOfMonth < computedMinDate) return true;
      if (computedMaxDate && firstDayOfMonth > computedMaxDate) return true;
      return false;
    },
    [calendarDate, computedMinDate, computedMaxDate]
  );

  const isPrevMonthDisabled = useMemo(() => {
    if (!computedMinDate) return false;
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const lastDayOfPrevMonth = new Date(Date.UTC(prevYear, prevMonth + 1, 0));
    return lastDayOfPrevMonth < computedMinDate;
  }, [computedMinDate, currentMonth, currentYear]);

  const isNextMonthDisabled = useMemo(() => {
    if (!computedMaxDate) return false;
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    const firstDayOfNextMonth = new Date(Date.UTC(nextYear, nextMonth, 1));
    return firstDayOfNextMonth > computedMaxDate;
  }, [computedMaxDate, currentMonth, currentYear]);

  // State for custom month/year dropdown menus
  const [isMonthMenuOpen, setIsMonthMenuOpen] = useState(false);
  const [isYearMenuOpen, setIsYearMenuOpen] = useState(false);
  const monthMenuRef = useRef<HTMLUListElement>(null);
  const yearMenuRef = useRef<HTMLUListElement>(null);
  const monthTriggerRef = useRef<HTMLDivElement>(null);
  const yearTriggerRef = useRef<HTMLDivElement>(null);

  const handleMonthSelect = useCallback((month: number) => {
    setCalendarDate((prev) => new Date(Date.UTC(prev.getUTCFullYear(), month, 1)));
    setIsMonthMenuOpen(false);
  }, []);

  const handleYearSelect = useCallback(
    (year: number) => {
      setCalendarDate((prev) => {
        let month = prev.getUTCMonth();
        // Clamp month if it becomes out of range at the new year boundary
        if (computedMinDate && year === computedMinDate.getUTCFullYear()) {
          month = Math.max(month, computedMinDate.getUTCMonth());
        }
        if (computedMaxDate && year === computedMaxDate.getUTCFullYear()) {
          month = Math.min(month, computedMaxDate.getUTCMonth());
        }
        return new Date(Date.UTC(year, month, 1));
      });
      setIsYearMenuOpen(false);
    },
    [computedMinDate, computedMaxDate]
  );

  // Close month/year menus on outside click
  useEffect(() => {
    if (!isMonthMenuOpen && !isYearMenuOpen) return;

    const handleMenuOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        isMonthMenuOpen &&
        !monthMenuRef.current?.contains(target) &&
        !monthTriggerRef.current?.contains(target)
      ) {
        setIsMonthMenuOpen(false);
      }
      if (
        isYearMenuOpen &&
        !yearMenuRef.current?.contains(target) &&
        !yearTriggerRef.current?.contains(target)
      ) {
        setIsYearMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMenuOutsideClick);
    return () => document.removeEventListener('mousedown', handleMenuOutsideClick);
  }, [isMonthMenuOpen, isYearMenuOpen]);

  // Scroll active year into view when year menu opens
  useEffect(() => {
    if (isYearMenuOpen && yearMenuRef.current) {
      const activeItem = yearMenuRef.current.querySelector('[data-active="true"]');
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
  }, [isYearMenuOpen]);

  /**
   * Format a date for display using the browser's locale formatting.
   * Uses timeZone: 'UTC' to ensure consistent date display regardless of user's timezone.
   */
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString(effectiveLocale, {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const normalizedSelectableDates = useMemo(() => {
    if (!Array.isArray(selectableDates)) {
      return [] as string[];
    }

    return selectableDates.filter((dateString): dateString is string => {
      return typeof dateString === 'string' && dateString.trim().length > 0;
    });
  }, [selectableDates]);

  const selectableDatesSet = useMemo(() => {
    const normalizedSet = new Set<string>();

    normalizedSelectableDates.forEach((dateString) => {
      const [year, month, day] = dateString.split('-').map(Number);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return;
      }

      const utc = new Date(Date.UTC(year, month - 1, day));
      normalizedSet.add(getNormalizedDate(utc));
    });

    return normalizedSet;
  }, [normalizedSelectableDates]);

  const calendarDays = useMemo((): CalendarDay[] => {
    const days: CalendarDay[] = [];
    const startOfMonth = new Date(Date.UTC(currentYear, currentMonth, 1));
    const endOfMonth = new Date(Date.UTC(currentYear, currentMonth + 1, 0));
    const daysInMonth = endOfMonth.getUTCDate();
    const firstWeekDay = (startOfMonth.getUTCDay() - startDay + 7) % 7;

    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = new Date(Date.UTC(prevYear, prevMonth + 1, 0)).getUTCDate();

    for (let i = firstWeekDay; i > 0; i--) {
      const day = daysInPrevMonth - i + 1;
      days.push({
        day,
        currentMonth: false,
        date: new Date(Date.UTC(prevYear, prevMonth, day)),
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        currentMonth: true,
        date: new Date(Date.UTC(currentYear, currentMonth, i)),
      });
    }

    const totalCells = days.length <= 35 ? 35 : 42;
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    const remaining = totalCells - days.length;

    for (let i = 1; i <= remaining; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: new Date(Date.UTC(nextYear, nextMonth, i)),
      });
    }

    return days;
  }, [currentYear, currentMonth, startDay]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      // Update ref before state to prevent useEffect from re-syncing
      lastSyncedValueRef.current = getNormalizedDate(date);
      setSelectedDate(date);
      setCalendarDate(date);
      setShowCalendar(false);
      onChange?.(date);
    },
    [onChange]
  );

  // Store onBlur in a ref so handleDocumentClick can access the latest value
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const handleDocumentClick = useCallback((e: MouseEvent) => {
    const target = e.target as Node;

    // Don't close if clicking inside the input area or the portaled calendar
    if (calendarRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
      return;
    }

    setShowCalendar(false);
    // Trigger validation when clicking outside without selecting
    onBlurRef.current?.();
  }, []);

  // Attach/detach outside click listener
  useEffect(() => {
    if (showCalendar) {
      document.addEventListener('mousedown', handleDocumentClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [showCalendar, handleDocumentClick]);

  useEffect(() => {
    // Handle null/empty value
    if (value === null || value === undefined || value === '') {
      if (lastSyncedValueRef.current === 'null') {
        return; // Already synced to null, skip
      }
      lastSyncedValueRef.current = 'null';
      setSelectedDate((prev) => (prev === null ? prev : null));
      return;
    }

    const nextDate = parseDateInput(value);
    if (Number.isNaN(nextDate.getTime())) {
      return;
    }

    const nextNormalized = getNormalizedDate(nextDate);

    // Skip if we already synced this date value
    if (lastSyncedValueRef.current === nextNormalized) {
      return;
    }

    // Update the ref to track we've synced this value
    lastSyncedValueRef.current = nextNormalized;

    setSelectedDate(nextDate);
    setCalendarDate(nextDate);
  }, [value]);

  const handlePrevMonth = () => {
    setCalendarDate((prev) => {
      const year = prev.getUTCFullYear();
      const month = prev.getUTCMonth();
      return new Date(Date.UTC(year, month - 1, 1));
    });
  };

  const handleNextMonth = () => {
    setCalendarDate((prev) => {
      const year = prev.getUTCFullYear();
      const month = prev.getUTCMonth();
      return new Date(Date.UTC(year, month + 1, 1));
    });
  };

  const sizeClassMap = {
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
    large: styles.sizeLarge,
  };

  return (
    <div
      className={classnames(styles.datePicker, styles[theme], sizeClassMap[size], {
        [styles[`validation-${validationResponse?.validationType}`]]:
          validationResponse?.validationType,
      })}
      ref={calendarRef}
    >
      {(label || helperText) && (
        <Box mb={4}>
          {label && (
            <label htmlFor={name}>
              <Typography
                className={styles.label}
                variant="label"
                size="small"
                direction={direction}
                theme={theme}
                color="gray-3"
              >
                {label}
                {required && '*'}
              </Typography>
            </label>
          )}
          {helperText && (
            <Typography
              className={styles.helperText}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              color="gray-5"
            >
              {helperText}
            </Typography>
          )}
        </Box>
      )}
      <Box
        ref={inputContainerRef}
        className={classnames(styles.inputContainer, styles[theme], {
          [styles.disabled]: disabled,
        })}
        onClick={() => !disabled && setShowCalendar(!showCalendar)}
      >
        <input
          readOnly
          disabled={disabled}
          id={name}
          name={name}
          type="text"
          data-date-picker
          placeholder={getLocalizedDatePlaceholder(effectiveLocale)}
          value={selectedDate ? formatDate(selectedDate) : ''}
          className={classnames(styles.dateInput, styles[theme], {
            [styles.disabled]: disabled,
          })}
        />
        {selectedDate && !disabled ? (
          <button
            type="button"
            className={styles.clearButton}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedDate(null);
              lastSyncedValueRef.current = 'null';
              setShowCalendar(false);
              onClear?.();
            }}
            data-testid={`${name}-clear-button`}
          >
            <Iconography
              category="global"
              name="close"
              color="white"
              theme={theme}
              size={{ xs: 16, md: 16, lg: 16 }}
            />
          </button>
        ) : null}
        <Iconography category="global" name="calendar" color="white" theme={theme} />
      </Box>

      {showCalendar &&
        createPortal(
          <Box
            className={classnames(styles.calendar, styles.portal, styles[theme])}
            ref={dropdownRef}
            tabIndex={-1}
            theme={theme}
            background="gray-9"
            // eslint-disable-next-line react/forbid-component-props -- Required for dynamic portal positioning
            style={{
              position: 'fixed',
              top: shouldOpenAbove ? 'auto' : `${calendarPosition.top}px`,
              bottom: shouldOpenAbove ? `${window.innerHeight - calendarPosition.top}px` : 'auto',
              left: `${calendarPosition.left}px`,
              minWidth: `${calendarPosition.width}px`,
              width: 'fit-content',
            }}
          >
            <div className={styles.calendarHeader}>
              <IconButton
                icon={{ category: 'arrows', name: 'chevron-left' }}
                size="small"
                handleClick={handlePrevMonth}
                disabled={isPrevMonthDisabled}
                theme={theme}
                gaComponent="DatePicker"
                dataTestId="date-picker-prev-month"
              />
              {navigationMode === 'dropdown' ? (
                <div className={styles.calendarDropdowns}>
                  {/* Month dropdown trigger */}
                  <div
                    className={classnames(styles.calendarDropdownContainer, styles.monthDropdown)}
                  >
                    <div
                      ref={monthTriggerRef}
                      className={classnames(
                        styles.calendarDropdownTrigger,
                        styles[`dropdownTrigger_${theme}`],
                        { [styles.dropdownTriggerOpen]: isMonthMenuOpen }
                      )}
                      onClick={() => {
                        setIsMonthMenuOpen((prev) => !prev);
                        setIsYearMenuOpen(false);
                      }}
                      role="combobox"
                      aria-expanded={isMonthMenuOpen}
                      aria-haspopup="listbox"
                      tabIndex={0}
                      data-testid="date-picker-month-dropdown"
                    >
                      <Typography variant="body" size="small" color="white" theme={theme}>
                        {monthNames[currentMonth]}
                      </Typography>
                      <Iconography
                        className={classnames({
                          [styles.dropdownIconRotated]: isMonthMenuOpen,
                        })}
                        category="arrows"
                        name="chevron-down"
                        theme={theme}
                        color="white"
                        size={{ xs: 16, md: 16, lg: 16 }}
                      />
                    </div>
                    {isMonthMenuOpen && (
                      <ul
                        ref={monthMenuRef}
                        className={classnames(
                          styles.calendarDropdownMenu,
                          styles[`dropdownMenu_${theme}`]
                        )}
                        role="listbox"
                        data-testid="date-picker-month-listbox"
                      >
                        {monthNames.map((monthName, index) => {
                          const isDisabledMonth = isMonthDisabledInDropdown(index);
                          const isActive = index === currentMonth;
                          return (
                            <li
                              key={monthName}
                              className={classnames(styles.calendarDropdownItem, {
                                [styles.calendarDropdownItemActive]: isActive,
                                [styles.calendarDropdownItemDisabled]: isDisabledMonth,
                              })}
                              role="option"
                              aria-selected={isActive}
                              aria-disabled={isDisabledMonth}
                              data-active={isActive}
                              onClick={() => !isDisabledMonth && handleMonthSelect(index)}
                            >
                              <Typography
                                variant="body"
                                size="small"
                                color={isActive ? 'black' : isDisabledMonth ? 'gray-6' : 'white'}
                                theme={theme}
                              >
                                {monthName}
                              </Typography>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {/* Year dropdown trigger */}
                  <div className={styles.calendarDropdownContainer}>
                    <div
                      ref={yearTriggerRef}
                      className={classnames(
                        styles.calendarDropdownTrigger,
                        styles[`dropdownTrigger_${theme}`],
                        { [styles.dropdownTriggerOpen]: isYearMenuOpen }
                      )}
                      onClick={() => {
                        setIsYearMenuOpen((prev) => !prev);
                        setIsMonthMenuOpen(false);
                      }}
                      role="combobox"
                      aria-expanded={isYearMenuOpen}
                      aria-haspopup="listbox"
                      tabIndex={0}
                      data-testid="date-picker-year-dropdown"
                    >
                      <Typography variant="body" size="small" color="white" theme={theme}>
                        {currentYear}
                      </Typography>
                      <Iconography
                        className={classnames({
                          [styles.dropdownIconRotated]: isYearMenuOpen,
                        })}
                        category="arrows"
                        name="chevron-down"
                        theme={theme}
                        color="white"
                        size={{ xs: 16, md: 16, lg: 16 }}
                      />
                    </div>
                    {isYearMenuOpen && (
                      <ul
                        ref={yearMenuRef}
                        className={classnames(
                          styles.calendarDropdownMenu,
                          styles[`dropdownMenu_${theme}`]
                        )}
                        role="listbox"
                        data-testid="date-picker-year-listbox"
                      >
                        {yearRange.map((year) => {
                          const isActive = year === currentYear;
                          return (
                            <li
                              key={year}
                              className={classnames(styles.calendarDropdownItem, {
                                [styles.calendarDropdownItemActive]: isActive,
                              })}
                              role="option"
                              aria-selected={isActive}
                              data-active={isActive}
                              onClick={() => handleYearSelect(year)}
                            >
                              <Typography
                                variant="body"
                                size="small"
                                color={isActive ? 'black' : 'white'}
                                theme={theme}
                              >
                                {year}
                              </Typography>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <Typography variant="button" size="medium" color="white" theme={theme}>
                  {calendarDate.toLocaleString(displayLocale, { month: 'long', timeZone: 'UTC' })}{' '}
                  {calendarDate.getUTCFullYear()}
                </Typography>
              )}
              <IconButton
                icon={{ category: 'arrows', name: 'chevron-right' }}
                size="small"
                handleClick={handleNextMonth}
                disabled={isNextMonthDisabled}
                theme={theme}
                gaComponent="DatePicker"
                dataTestId="date-picker-next-month"
              />
            </div>

            <Box className={styles.calendarGrid}>
              {weekdays.map((wd: string) => (
                <Typography
                  key={wd}
                  className={`${styles.calendarCell} ${styles.calendarWeekday}`}
                  variant="label"
                  size="small"
                  color="gray-5"
                  theme={theme}
                >
                  {wd}
                </Typography>
              ))}

              {calendarDays.map(({ day, currentMonth, date }) => {
                const isSelected = selectedDate
                  ? getNormalizedDate(selectedDate) === getNormalizedDate(date)
                  : getNormalizedDate(currentDate) === getNormalizedDate(date);
                const isAvailable = selectableDatesSet.has(getNormalizedDate(date));
                const isPastMinDate = isBeforeMinDate(date);
                const isFutureMaxDate = isAfterMaxDate(date);
                const allowsSelection = normalizedSelectableDates.length > 0;
                const isDisabled =
                  isPastMinDate || isFutureMaxDate || (!isAvailable && allowsSelection);

                return (
                  <Typography
                    key={date.toISOString()}
                    className={classnames(styles.calendarCell, styles.day, styles[theme], {
                      [styles.isAvailable]: isAvailable && allowsSelection,
                      [styles.selected]: isSelected && !isDisabled,
                      [styles.notCurrentMonth]: !currentMonth,
                      [styles.disabled]: isDisabled,
                    })}
                    onClick={() => {
                      if (isDisabled) return;
                      if (allowsSelection) {
                        isAvailable && handleSelectDate(date);
                      } else {
                        handleSelectDate(date);
                      }
                    }}
                    variant="body"
                    size="small"
                  >
                    {day}
                  </Typography>
                );
              })}
            </Box>
          </Box>,
          document.body
        )}

      {showValidation && (
        <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
          {validationResponse?.message && (
            <Typography
              className={classnames(styles.validationMessage)}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              id={`${name}-validation-message`}
            >
              {validationResponse.message || ''}
            </Typography>
          )}
        </Box>
      )}
    </div>
  );
};

// Custom comparison to prevent unnecessary re-renders
const arePropsEqual = (prevProps: DatePickerProps, nextProps: DatePickerProps): boolean => {
  // Compare primitive props
  if (
    prevProps.name !== nextProps.name ||
    prevProps.theme !== nextProps.theme ||
    prevProps.direction !== nextProps.direction ||
    prevProps.size !== nextProps.size ||
    prevProps.label !== nextProps.label ||
    prevProps.helperText !== nextProps.helperText ||
    prevProps.required !== nextProps.required ||
    prevProps.locale !== nextProps.locale ||
    prevProps.language !== nextProps.language ||
    prevProps.showValidation !== nextProps.showValidation ||
    prevProps.disabled !== nextProps.disabled ||
    prevProps.navigationMode !== nextProps.navigationMode ||
    prevProps.onClear !== nextProps.onClear ||
    prevProps.onChange !== nextProps.onChange ||
    prevProps.onBlur !== nextProps.onBlur
  ) {
    return false;
  }

  // Compare validation response
  if (
    prevProps.validationResponse?.validationType !== nextProps.validationResponse?.validationType ||
    prevProps.validationResponse?.message !== nextProps.validationResponse?.message
  ) {
    return false;
  }

  // Compare value - normalize both to string for comparison
  const prevValue = prevProps.value;
  const nextValue = nextProps.value;

  if (prevValue === nextValue) {
    return true;
  }

  // Both null/undefined/empty
  if (!prevValue && !nextValue) {
    return true;
  }

  // One is null/undefined/empty, the other is not
  if (!prevValue || !nextValue) {
    return false;
  }

  // Compare normalized date strings
  try {
    const prevNormalized = getNormalizedDate(parseDateInput(prevValue));
    const nextNormalized = getNormalizedDate(parseDateInput(nextValue));
    return prevNormalized === nextNormalized;
  } catch {
    return false;
  }
};

export default React.memo(DatePicker, arePropsEqual);
